import {
  Component,
  OnInit,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from '../../app.service';
import { CourseModel } from '../../models/course.model';
import { ApiRequestService } from '../../shared/api-request.service';
import { UtilsService } from '../../shared/utils.service';
import { RBACService } from '../../shared/rbac.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

@Component({
  selector: 'app-courses-edit',
  templateUrl: './courses-edit.component.html',
  styleUrls: ['./courses-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class CoursesEditComponent implements OnInit {
  public induction: CourseModel;
  inProgress = false;

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  canDelete: boolean = false;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    public rbac: RBACService,
    private dialogRef: MatDialogRef<CoursesEditComponent>,
    @Inject(MAT_DIALOG_DATA) private data: { course: CourseModel }
  ) {}

  ngOnInit() {
    // Resolve RBAC permission flags.
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    this.induction = this.data.course;
  }

  onSubmit(form: NgForm) {
    if (this.inProgress) {
      return;
    }

    if (!form.valid) {
      this.utils.showModal(
        'Form Validation Error',
        'Please make sure you enter all fields with valid information.'
      );
      return;
    }

    if (this.induction.id) {
      if (this.induction.reset_status && this.induction.induction_enabled) {
        this.utils.showModal(
          'Resetting Induction Statuses',
          'Are you sure you want to reset the induction statuses? This will reset the induction status for all your staff, ' +
            this.utils.getLangTerm('contractors.plural', 'Contractors') +
            ' and their ' +
            this.utils.getLangTerm('employees.plural', 'Employees') +
            ' resulting in incomplete induction statuses for this induction. They will need to complete and submit this induction again.',
          () => {
            this.inProgress = true;
            this.update();
          }
        );
      } else {
        this.inProgress = true;
        this.update();
      }
    } else {
      this.inProgress = true;
      this.create();
    }
  }

  onRemove() {
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_INDUCTIONS,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete inductions.'
      );
      return;
    }

    this.utils.showModal(
      'Remove Induction',
      'Are you sure you want to remove this induction?',
      () => {
        this.api
          .makeRequest('delete', `v1/inductions/${this.induction.id}`)
          .then((response: any) => {
            this.utils.showToast('The induction was removed.');
            this.dialogRef.close(true);
          });
      }
    );
  }

  private update() {
    if (this.induction.reset_status && !this.induction.induction_enabled) {
      this.induction.reset_status = false; // If the reset status is enabled but the induction is disabled then this should be disabled when submitting the form
    }

    this.api
      .makeRequest('put', `v1/inductions/${this.induction.id}`, this.induction)
      .then((response: any) => {
        this.utils.showToast('The induction was updated.');
        this.dialogRef.close(true);
        this.inProgress = false;
      })
      .catch((err: any) => {
        this.inProgress = false;
      });
  }

  private create() {
    this.api
      .makeRequest('post', 'v1/inductions', this.induction)
      .then((response: any) => {
        this.utils.showToast(
          'New induction added. Please continue to add modules and questions.'
        );
        this.dialogRef.close(true);
        this.inProgress = false;
      })
      .catch((err: any) => {
        this.inProgress = false;
      });
  }

  protected readonly RBACService = RBACService;
}
